import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Loader2,
  AlertCircle,
  ArrowLeft,
  Search,
  CreditCard,
  UserX,
  UserCheck,
  RefreshCw,
} from 'lucide-react';
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  deactivateUser,
  reactivateUser,
  cancelUserSubscription,
  syncUserSubscription,
  syncAllSubscriptions,
} from '../api/client';
import { Pagination } from '../components/Pagination';
import type { User, UserCreate, UserUpdate } from '../types';

const ITEMS_PER_PAGE = 10;

export function AdminUsers() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Cancel-subscription modal state
  const [subscriptionUser, setSubscriptionUser] = useState<User | null>(null);
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  // Deactivate modal state
  const [deactivateUserState, setDeactivateUserState] = useState<User | null>(null);
  const [deactivateAlsoCancelSub, setDeactivateAlsoCancelSub] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  // Stripe sync state
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Per-row pending state for the reactivate quick-action
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);

  // Search and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  // Filter and paginate users
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(query) ||
        user.id.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = useMemo(() => {
    const start = currentPage * ITEMS_PER_PAGE;
    return filteredUsers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(0);
  }, [searchQuery]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({ email: '', password: '' });
    setShowModal(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({ email: user.email, password: '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingUser) {
        // Update existing user
        const updateData: UserUpdate = {};
        if (formData.email !== editingUser.email) {
          updateData.email = formData.email;
        }
        await updateUser(editingUser.id, updateData);
      } else {
        // Create new user
        const createData: UserCreate = {
          email: formData.email,
          password: formData.password,
        };
        await createUser(createData);
      }
      setShowModal(false);
      loadUsers();
    } catch (err) {
      setError(editingUser ? 'Failed to update user' : 'Failed to create user');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId: string) => {
    setDeleting(true);
    try {
      await deleteUser(userId);
      setDeleteConfirm(null);
      loadUsers();
    } catch (err) {
      setError('Failed to delete user');
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    if (user.is_active) {
      // Going active -> inactive: open deactivate modal so admin can also choose
      // whether to cancel the Stripe subscription in the same action.
      setDeactivateUserState(user);
      setDeactivateAlsoCancelSub(false);
      return;
    }
    try {
      setReactivatingId(user.id);
      await reactivateUser(user.id);
      loadUsers();
    } catch (err) {
      setError('Failed to reactivate user');
      console.error(err);
    } finally {
      setReactivatingId(null);
    }
  };

  const handleConfirmDeactivate = async () => {
    if (!deactivateUserState) return;
    setDeactivating(true);
    try {
      await deactivateUser(deactivateUserState.id, {
        cancel_subscription: deactivateAlsoCancelSub,
      });
      setDeactivateUserState(null);
      loadUsers();
    } catch (err) {
      setError('Failed to deactivate user');
      console.error(err);
    } finally {
      setDeactivating(false);
    }
  };

  const handleConfirmCancelSubscription = async () => {
    if (!subscriptionUser) return;
    setCancellingSubscription(true);
    try {
      await cancelUserSubscription(subscriptionUser.id, {
        immediately: cancelImmediately,
      });
      setSubscriptionUser(null);
      setCancelImmediately(false);
      loadUsers();
    } catch (err) {
      const message = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(message || 'Failed to cancel subscription');
      console.error(err);
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleSyncUser = async (user: User) => {
    setSyncingId(user.id);
    setSyncMessage(null);
    try {
      const updated = await syncUserSubscription(user.id);
      const before = user.subscription_status || 'none';
      const after = updated.subscription_status || 'none';
      setSyncMessage(
        before === after
          ? `${user.email}: already in sync with Stripe (${after.replace(/_/g, ' ')})`
          : `${user.email}: ${before.replace(/_/g, ' ')} → ${after.replace(/_/g, ' ')}`
      );
      loadUsers();
    } catch (err) {
      const message = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(message || 'Failed to sync subscription from Stripe');
      console.error(err);
    } finally {
      setSyncingId(null);
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    setSyncMessage(null);
    try {
      const summary = await syncAllSubscriptions();
      const parts = [
        `${summary.changed} of ${summary.total} users updated from Stripe`,
        summary.unmatched ? `${summary.unmatched} not found in Stripe` : null,
        summary.errors ? `${summary.errors} errors` : null,
        summary.sites_suspended ? `${summary.sites_suspended} site(s) switched off (unsubscribed)` : null,
        summary.sites_restored ? `${summary.sites_restored} site(s) switched back on` : null,
      ].filter(Boolean);
      setSyncMessage(parts.join(', '));
      loadUsers();
    } catch (err) {
      const message = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(message || 'Failed to sync subscriptions from Stripe');
      console.error(err);
    } finally {
      setSyncingAll(false);
    }
  };

  const subscriptionLabel = (user: User): string => {
    if (!user.subscription_status && !user.stripe_customer_id) return 'No subscription';
    if (!user.subscription_status) return 'Pending';
    return user.subscription_status.replace(/_/g, ' ');
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Dashboard
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-brand-orange" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              User Management
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSyncAll}
              disabled={syncingAll}
              title="Pull every user's customer, subscription and status from Stripe"
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {syncingAll ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Sync from Stripe
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add User
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by email or ID..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} matching "{searchQuery}"
          </p>
        )}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stripe sync result */}
      {syncMessage && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-3 text-green-700 dark:text-green-400">
          <Check className="w-5 h-5 flex-shrink-0" />
          <span>{syncMessage}</span>
          <button onClick={() => setSyncMessage(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-orange animate-spin" />
        </div>
      ) : (
        /* Users Table */
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Subscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {searchQuery
                      ? `No users found matching "${searchQuery}".`
                      : 'No users found. Click "Add User" to create one.'}
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => {
                  const hasSubscription = !!user.stripe_subscription_id || !!user.stripe_customer_id;
                  const subStatus = user.subscription_status || '';
                  const subAlreadyCancelled =
                    subStatus === 'cancelled' || subStatus === 'canceled';
                  return (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.email}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {user.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(user)}
                        disabled={reactivatingId === user.id}
                        title={user.is_active ? 'Click to deactivate' : 'Click to reactivate'}
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        } disabled:opacity-50`}
                      >
                        {reactivatingId === user.id && (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        )}
                        {user.is_active ? 'Active' : 'Deactivated'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                          subStatus === 'active' || subStatus === 'trialing'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : subAlreadyCancelled
                            ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}
                      >
                        {subscriptionLabel(user)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(user.created_at).toLocaleDateString('en-AU')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => handleEdit(user)}
                        title="Edit email"
                        className="text-brand-orange hover:text-brand-orange-dark mr-3"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleSyncUser(user)}
                        disabled={syncingId === user.id || syncingAll}
                        title="Sync subscription status from Stripe"
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mr-3 disabled:opacity-50"
                      >
                        {syncingId === user.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                      </button>
                      {hasSubscription && !subAlreadyCancelled && (
                        <button
                          onClick={() => {
                            setSubscriptionUser(user);
                            setCancelImmediately(false);
                          }}
                          title="Cancel Stripe subscription"
                          className="text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300 mr-3"
                        >
                          <CreditCard className="w-4 h-4" />
                        </button>
                      )}
                      {user.is_active ? (
                        <button
                          onClick={() => {
                            setDeactivateUserState(user);
                            setDeactivateAlsoCancelSub(false);
                          }}
                          title="Deactivate account"
                          className="text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 mr-3"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleToggleActive(user)}
                          disabled={reactivatingId === user.id}
                          title="Reactivate account"
                          className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 mr-3 disabled:opacity-50"
                        >
                          <UserCheck className="w-4 h-4" />
                        </button>
                      )}
                      {deleteConfirm === user.id ? (
                        <span className="inline-flex items-center gap-2">
                          <button
                            onClick={() => handleDelete(user.id)}
                            disabled={deleting}
                            className="text-red-600 hover:text-red-700"
                          >
                            {deleting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="text-gray-400 hover:text-gray-500"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(user.id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredUsers.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
            itemLabel="users"
          />
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingUser ? 'Edit User' : 'Create User'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                  placeholder="user@example.com"
                />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent"
                    placeholder="Enter password"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.email || (!editingUser && !formData.password)}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark rounded-full transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingUser ? (
                  'Save Changes'
                ) : (
                  'Create User'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {subscriptionUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Cancel Stripe Subscription
              </h2>
              <button
                onClick={() => setSubscriptionUser(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Cancel the Stripe subscription for{' '}
                <span className="font-medium">{subscriptionUser.email}</span>?
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Current status: <span className="capitalize">{subscriptionLabel(subscriptionUser)}</span>
              </p>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 pt-1">
                <input
                  type="checkbox"
                  checked={cancelImmediately}
                  onChange={(e) => setCancelImmediately(e.target.checked)}
                  className="rounded"
                />
                Cancel immediately (otherwise cancels at end of current billing period)
              </label>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setSubscriptionUser(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleConfirmCancelSubscription}
                disabled={cancellingSubscription}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {cancellingSubscription ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : cancelImmediately ? (
                  'Cancel Now'
                ) : (
                  'Cancel at Period End'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate User Modal */}
      {deactivateUserState && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Deactivate Account
              </h2>
              <button
                onClick={() => setDeactivateUserState(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Deactivate <span className="font-medium">{deactivateUserState.email}</span>?
              </p>
              <ul className="text-xs text-gray-500 dark:text-gray-400 list-disc pl-5 space-y-1">
                <li>The user will not be able to log in.</li>
                <li>All of their sites will be disabled, stopping telemetry, price polling, optimization and battery control.</li>
                <li>Account data is preserved and can be reactivated later.</li>
              </ul>
              {(deactivateUserState.stripe_customer_id || deactivateUserState.stripe_subscription_id) &&
                deactivateUserState.subscription_status !== 'cancelled' &&
                deactivateUserState.subscription_status !== 'canceled' && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 pt-1">
                    <input
                      type="checkbox"
                      checked={deactivateAlsoCancelSub}
                      onChange={(e) => setDeactivateAlsoCancelSub(e.target.checked)}
                      className="rounded"
                    />
                    Also cancel Stripe subscription at end of period
                  </label>
                )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setDeactivateUserState(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeactivate}
                disabled={deactivating}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {deactivating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
